import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import {
  useOrganizationAlertOccurrences,
  useUserAlertOccurrences,
} from '@/hooks/api/useGroupedAlerts';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ApiError } from '@kirin/api-client/errors';
import { AlertDetailContent } from '@/components/grouped-alerts/AlertDetailContent';
import { AlertOccurrencesModal } from '@/components/grouped-alerts/AlertOccurrencesModal';
import { AlertWindowContext } from '@/components/grouped-alerts/session-view/session-view-link';
import { Loading } from '@/components/shared/placeholders/Loading';
import { ErrorFallback } from '@/components/shared/placeholders/Error';

// The parent alert is all this page shows; one occurrence keeps the read small.
const ONE_OCCURRENCE = { page: 1, limit: 1 };

/**
 * One alert's detail in its own browser window, opened by the alerts tables'
 * pop-out button. Routed outside the app layout, so it is just the panel.
 * Reads the alert through the occurrences endpoint, which returns the grouped
 * alert (with mute, exclusion, severity override and feedback) for one key.
 */
export default function AlertWindowPage(): ReactElement {
  const { organizationId, alertKey } = useParams<{
    organizationId?: string;
    alertKey: string;
  }>();
  useDocumentTitle('Alert details');
  const [showInstances, setShowInstances] = useState(false);
  const orgQuery = useOrganizationAlertOccurrences(
    organizationId,
    alertKey,
    ONE_OCCURRENCE,
    !!organizationId
  );
  const userQuery = useUserAlertOccurrences(
    alertKey,
    ONE_OCCURRENCE,
    !organizationId
  );
  const { data, isError, error, refetch } = organizationId
    ? orgQuery
    : userQuery;
  // A 404/403 is final; anything else (5xx, network) is worth a retry.
  const isGone =
    error instanceof ApiError && (error.status === 404 || error.status === 403);

  return (
    <main className="min-h-screen bg-background p-6">
      {/* No data and no error covers a fetch paused while offline too. */}
      {!data && !isError ? (
        <Loading label="Loading alert…" className="min-h-0 py-8" />
      ) : !data ? (
        isGone ? (
          <ErrorFallback label="This alert was deleted, or you do not have access to it." />
        ) : (
          <ErrorFallback
            label="Could not load this alert."
            retry={() => void refetch()}
          />
        )
      ) : (
        <AlertWindowContext.Provider value>
          <AlertDetailContent
            alert={data.alert}
            {...(organizationId ? { organizationId } : {})}
            scope={
              organizationId
                ? { kind: 'organization', organizationId }
                : { kind: 'account' }
            }
            alertKey={data.alert.alertKey}
            onShowOccurrences={() => setShowInstances(true)}
            standalone
          />
          {/* The table's instances dialog, opened in place: this window has
              no app layout to route it through. */}
          {showInstances && (
            <AlertOccurrencesModal
              open
              alertKey={data.alert.alertKey}
              {...(organizationId ? { organizationId } : {})}
              onClose={() => setShowInstances(false)}
            />
          )}
        </AlertWindowContext.Provider>
      )}
    </main>
  );
}
